import { fromPairs } from 'lodash';
import { useMemo } from 'react';
import { useUserCustomFieldsAsFilterableFields } from 'components/organisms/module-settings/team-members/section-field/hooks';
import {
  FieldType,
  FilterableField,
  SingleSelectField,
  SingleUserField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { useAccount } from 'hooks/use-account';
import { useActiveOrInvitedNonCustomerUsersQuery } from 'queries/members';
import { useGetAllPartnerCompanies } from 'queries/partner';
import { useVendorRolesQuery } from 'queries/roles';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { sortByText } from 'utils/sort-by-text';

const FIELD_ID_USER = -1000;
const FIELD_ID_ROLE = -1001;
const FIELD_ID_COMPANY = -1002;

export const useUserFieldsAsFilterableFields = (): {
  fields: FilterableField[];
  isFieldsLoading: boolean;
} => {
  const { data: users, isLoading: isUserLoading } =
    useActiveOrInvitedNonCustomerUsersQuery();
  const { company: vendorCompany } = useAccount();
  const sortedUsers = useMemo(
    () =>
      (users ?? []).sort((userA, userB) =>
        sortByText(fullName(userA), fullName(userB)),
      ),
    [users],
  );
  const { data: partnerCompanies, isLoading: isCompaniesLoading } =
    useGetAllPartnerCompanies();

  const { data: roles, isLoading: isRoleLoading } = useVendorRolesQuery();
  const roleOptions = useMemo(
    () =>
      (roles ?? []).map(({ roleId, roleName }) => ({
        label: roleName,
        value: roleId,
        color: getRandomColor(roleId),
        active: true,
      })),
    [roles],
  );
  const userField: SingleUserField = useMemo(
    () => ({
      fieldId: FIELD_ID_USER,
      fieldName: 'userId',
      fieldLabel: 'Team member',
      type: FieldType.SingleUser,
      private: false,
      userCreated: false,
      metaData: {
        users: {
          by_user_id: fromPairs(sortedUsers.map((x) => [x.userId, x])),
          active_user_ids: sortedUsers.map((x) => x.userId),
          by_project_id: {},
        },
      },
      sourceType: SourceType.User,
    }),
    [sortedUsers],
  );
  const roleField: SingleSelectField = useMemo(
    () => ({
      fieldId: FIELD_ID_ROLE,
      fieldName: 'role',
      fieldLabel: 'Role',
      type: FieldType.SingleSelect,
      private: false,
      userCreated: false,
      metaData: {
        options: roleOptions,
      },
      sourceType: SourceType.User,
    }),
    [roleOptions],
  );
  const _companyField: SingleSelectField = useMemo(
    () => ({
      fieldId: FIELD_ID_COMPANY,
      fieldName: 'companyId',
      fieldLabel: 'Organisation',
      type: FieldType.SingleSelect,
      private: false,
      userCreated: false,
      metaData: {
        required: true,
        options: [
          {
            label: vendorCompany.companyName,
            value: vendorCompany.companyId,
            color: getRandomColor(vendorCompany.companyId),
            active: true,
          },
        ].concat(
          (partnerCompanies ?? [])
            .filter(({ active }) => active)
            .map(({ companyId, companyName }) => ({
              label: companyName,
              value: companyId,
              color: getRandomColor(companyId),
              active: true,
            }))
            .sort(byLabel),
        ),
      },
      sourceType: SourceType.User,
    }),
    [partnerCompanies, vendorCompany.companyId, vendorCompany.companyName],
  );

  const { isLoading: isUserCustomFieldsLoading, fields: userCustomFields } =
    useUserCustomFieldsAsFilterableFields();

  return useMemo(
    () => ({
      fields: [
        userField,
        roleField,
        // ...((partnerCompanies?.length ?? 0) !== 0 ? [companyField] : []),
        ...userCustomFields,
      ],
      isFieldsLoading:
        isUserLoading ||
        isRoleLoading ||
        isUserCustomFieldsLoading ||
        isCompaniesLoading,
    }),
    [
      userField,
      roleField,
      // partnerCompanies?.length,
      // companyField,
      userCustomFields,
      isUserLoading,
      isRoleLoading,
      isUserCustomFieldsLoading,
      isCompaniesLoading,
    ],
  );
};
